import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { LeverageInput } from './LeverageInput/LeverageInput';
import { useBorrowAmountStyle } from './BorrowAmount.style';
import { RiskFactorFields } from '../RiskFactor';

import { useModal } from '@/features/Modals';
import { InfoCard } from '@/shared/ui/InfoCard';
import { LeverageStrategy } from '@/shared/validations/borrowModal.schema';
import { useGetMaxBorrow } from '@/shared/hooks/useGetMaxBorrow';
import { Agreement, Token } from '@/shared/api/borrow';

type BorrowAmountProps = {
    agreement: Agreement;
    collateralAssets: Token[];
};

export enum BorrowAmountFields {
    ASSET_AMOUNT = 'assetAmount',
    MAX_BORROW = 'maxBorrow',
}

const BorrowAmount = ({ agreement, collateralAssets }: BorrowAmountProps) => {
    const classes = useBorrowAmountStyle();

    const { control, setValue, trigger } = useFormContext();

    const { data: maxBorrow, loading } = useGetMaxBorrow({ agreement });

    useEffect(() => {
        setValue(BorrowAmountFields.MAX_BORROW, maxBorrow);
    }, [maxBorrow]);

    const { modalProps } = useModal();
    const { tokenSymbol } = modalProps || {};

    const { assetSymbol, borrowApy, decimals } = {
        assetSymbol: tokenSymbol,
        borrowApy: agreement.apy,
        decimals: agreement.leverage.decimals,
    };

    const noCollateralAssets = !collateralAssets?.some(
        (item: { balance?: any }) => item.balance !== undefined && Number(item.balance) > 0,
    );

    const isAssesAvailable = Number(maxBorrow) !== 0;

    return (
        <div>
            <div className={classes.text}>How much do you want to borrow?</div>
            <Controller
                control={control}
                name={BorrowAmountFields.ASSET_AMOUNT}
                render={({ field, fieldState: { error } }) => (
                    <>
                        <LeverageInput
                            tokenSymbol={assetSymbol}
                            value={field.value}
                            setValue={async (value) => {
                                field.onChange(value);
                                setValue(RiskFactorFields.STRATEGY, LeverageStrategy.MANUAL);
                                await trigger(BorrowAmountFields.ASSET_AMOUNT);
                            }}
                            disabled={noCollateralAssets || Number(maxBorrow) === 0}
                            apy={borrowApy}
                            decimals={decimals}
                            error={Boolean(error?.message)}
                            maxBorrow={maxBorrow}
                        />
                    </>
                )}
            />
            {!isAssesAvailable && !loading && (
                <InfoCard
                    type={InfoCard.types.ALERT}
                    text='No assets available to borrow.'
                    classes={{ root: classes.noCollateralAlert }}
                />
            )}
            {noCollateralAssets && (
                <InfoCard
                    type={InfoCard.types.ALERT}
                    text='No collateralized assets found. To continue borrowing, please make sure to deposit enough collateral into your account.'
                    classes={{ root: classes.noCollateralAlert }}
                />
            )}
        </div>
    );
};

export default BorrowAmount;
